// 批次 45（#9，issue #9）：exam 參數相依三頁的行為元件（KNOWN-NOT-DONE B3-2 結案）。
//
// 三頁的 legacy 捕捉態＝**錯誤頁**（單獨開啟無參數＝錯誤頁；R-diff 材料不對等）——
// 鏡像證據＝JSP 原始碼（§7 第 3 級）＋page-behaviors 測試；R-diff 不適用（登記）。
//
// ── QuestionManageBody（exam-question-manage，origin exam/questionManage.jsp 866 行）──
// 頁型 crud-list＋complexBody（CrudListPage 早退先例）。路徑 /exam/question/:questionBankId
// （react 路由自帶參數）；導流入口＝questionBank 列表「編輯」欄（questionBank.jsp:70-76/:119）。
//   - 查詢 form（:626-695，Enter 送出）：題庫名稱：label＋題庫名（route 參數查題庫列）＋
//     題目類型 select（全部-1＋五題型）＋狀態 select（全部-1/啟用1 selected/停用0）＋
//     按鈕列（回題庫管理→站內導航 questionBankMain :657／查詢 :659／刪除 :662）＋
//     **匯入題庫資料 in-form**（:670-692：label＋file＋檔案匯入＋使用說明+範本下載+ul×2＝
//     importBlockInForm 泛用 slot 慣例）。
//   - 表格（:699-712）6 欄：checkAll 全選 header checkbox＋No./題目類型/題目/難度/狀態。
//   - dialogModal（:720-866）＝新增/編輯題目大 modal：題目類型/題目內容(RichText→文字域鏡像)/
//     題目圖檔/選項群(新增項目)/建議答案/困難度 radio×3/答案(是非)…——題型相依區段
//     【雛形值待實機校準】（B3-2 誠實標註：五題型的完整區段切換＝接真後端批補）。
//
// ── InitAllQuestionBody（exam-init-all-question，origin exam/initAllQuestion.jsp 604 行）──
// 題庫匯入精靈第二步（questionBank.jsp:126-134 下一步 → exam/initAllQuestion/{id}/{count×5}）。
// 每題一個區塊（:455-597）：題目種類 select/題目內容/題目圖檔/建議答案/選項群（新增項目）/
// 困難度 radio×3/答案(是非)/是否啟用；底部 關閉(→題庫列表 :447)/存檔(save :448)。
// 雛形＝**單題區塊**鏡像【雛形值待實機校準】（多題迭代＝接真後端批）。
//
// ── ViewTestingBody（exam-view-testing-view-testing，origin exam/viewTesting.jsp 450 行）──
// 填寫測驗（prompt decorator 頁）。測驗資訊 ibox（:34-46 h5 測驗資訊＋狀態/參數插值）＋
// 作答表單（:101+，hidden id/examInstId/testingMode）＋按鈕群（:126-129 暫存/交卷/關閉/批改
// ——mode 相依顯示；雛形以 關閉＋暫存 顯示鏡像捕捉態【雛形值待實機校準】）。
//
// 【誠實標註】三頁＝0 契約頁（legacy 捕捉＝錯誤頁）；結構/行為以 JSP 行號為基準；
// mock 資料＝種子【雛形值待實機校準】；題型相依區段切換與作答互動＝接真後端批（#19）。
//
// 【證據等級】§7 第 3 級（原始碼，行號親讀 2026-10-02）。

import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { ContentPanel, ModalFrame, useDialogs } from '@/components/layouts';
import { DataTableShell } from '@/components/layouts/DataTableShell';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { t } from '@/messages/zh-TW';
import { DEFAULT_PAGE_SIZE, useDataSource, useList, type Row } from '@/mocks';
import { rowUiKey, toUiKey } from '../templates/shared';

/** 題型五種（questionManage.jsp case ynId/selectId/qaId/sqaId/multiselId；代碼值【雛形值待實機校準】）。 */
const QUESTION_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'yn', label: '是非題' },
  { value: 'select', label: '單選題' },
  { value: 'multisel', label: '複選題' },
  { value: 'qa', label: '問答題' },
  { value: 'sqa', label: '簡答題' },
];


// ── QuestionManageBody ──────────────────────────────────────────────────────
export function QuestionManageBody({ pageId }: { pageId: string }): ReactElement {
  const navigate = useNavigate();
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  const [questionType, setQuestionType] = useState('-1');
  const [enableStatus, setEnableStatus] = useState('1');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { data, loading, reload } = useList(pageId, {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    keyword: '',
    filters,
  });
  const rows: readonly Row[] = data?.rows ?? [];
  const [editOpen, setEditOpen] = useState(false);

  // 題庫名稱：route 參數（questionManage.jsp:631 ${questionBank.name}；以 fixtures 誠實值呈現）。
  const bankName = '臨床核心訓練題庫【雛形值待實機校準】';

  function doQuery(): void {
    // query（:317 getQuestionListAjax）；filters 直送（matchesFilters 種子過濾＝批次 15 慣例）。
    setFilters({ questionType, enableStatus });
    reload();
  }

  const columns: readonly TableColumn[] = [
    // 表頭（:701-708）：checkAll 全選＋No./題目類型/題目/難度/狀態。
    {
      key: 'checkAll',
      header: (
        <>
          {'全選 '}
          <input type="checkbox" id="checkAll" data-ui-id={`${pageId}-check-all`} className="size-4 accent-brand" />
        </>
      ),
      align: 'center',
    },
    { key: 'no', header: 'No.', align: 'center' },
    { key: 'questionType', header: '題目類型', align: 'center' },
    { key: 'content', header: '題目', align: 'left' },
    { key: 'level', header: '難度', align: 'center' },
    { key: 'enableStatus', header: '狀態', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ContentPanel uiId={`${pageId}-panel`} title="編輯題目" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 新增（:622 showModal(0)）。 */}
        <div className="flex flex-wrap items-center gap-2">
          <Button uiId={`${pageId}-add-btn`} variant="primary" size="sm" type="button" onClick={() => setEditOpen(true)}>
            新增
          </Button>
        </div>
        {/* 查詢 form（:626-695）。 */}
        <form
          data-ui-id={`${pageId}-search`}
          noValidate
          className="mt-3 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            doQuery();
          }}
        >
          <div data-ui-id={`${pageId}-search-fields`} className="grid gap-x-6 gap-y-3 md:grid-cols-2">
            <div>
              {/* 題庫名稱（:629-632：static 文字值）。 */}
              <label className="mb-[5px] inline-block font-bold text-ink">題庫名稱：</label>
              <div className="text-sm text-ink">{bankName}</div>
              {/* 題目類型（:635-643）。 */}
              <label className="mb-[5px] inline-block font-bold text-ink">題目類型：</label>
              <Select
                uiId={`${pageId}-search-question-type`}
                name="questionType"
                value={questionType}
                onChange={setQuestionType}
                options={[{ value: '-1', label: '全部' }, ...QUESTION_TYPE_OPTIONS]}
              />
              {/* 狀態（:646-652）。 */}
              <label className="mb-[5px] inline-block font-bold text-ink">狀態：</label>
              <Select
                uiId={`${pageId}-search-enable-status`}
                name="enableStatus"
                value={enableStatus}
                onChange={setEnableStatus}
                options={[
                  { value: '-1', label: '全部' },
                  { value: '1', label: '啟用' },
                  { value: '0', label: '停用' },
                ]}
              />
              {/* 按鈕列（:656-664）。 */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  uiId={`${pageId}-back-btn`}
                  variant="default"
                  size="sm"
                  type="button"
                  // :657 document.location.assign('exam/questionBankMain')＝回題庫管理；站內導航。
                  onClick={() => navigate('/exam/questionBank')}
                >
                  回題庫管理
                </Button>
                <Button uiId={`${pageId}-search-submit`} variant="default" size="sm" type="button" onClick={doQuery}>
                  查詢
                </Button>
                <Button
                  uiId={`${pageId}-bulk-delete`}
                  variant="danger"
                  size="sm"
                  type="button"
                  onClick={() => {
                    void (async () => {
                      // :662 deleteQuestion（批量刪除；未勾選→legacy 由 JS 檢查）。
                      await dialogs.alert('請先勾選要刪除的題目【雛形值待實機校準】');
                    })();
                  }}
                >
                  刪除
                </Button>
              </div>
            </div>
            {/* 匯入題庫資料（:670-692）＋使用說明（in-form）。 */}
            <div>
              <label className="mb-[5px] inline-block font-bold text-ink">匯入題庫資料：</label>
              <input type="file" data-ui-id={`${pageId}-import-file`} className="text-xs" accept=".xlsx" />
              <Button uiId={`${pageId}-import-submit`} variant="primary" size="sm" type="button">
                檔案匯入
              </Button>
              <div className="mt-2">
                {'使用說明: '}
                <Button uiId={`${pageId}-import-template`} variant="default" size="sm" type="button">
                  範本下載
                </Button>
                <ul className="ml-4 text-sm text-ink">
                  <li>資料由第四列開始，題目若已存在，則不重覆匯入。</li>
                  <li>Excel欄位從左到右欄位說明： 題目類型、題目、選項數量、選項、正確答案、難度、是否啟用</li>
                </ul>
              </div>
            </div>
          </div>
        </form>
        {/* 表格（:699-712）。 */}
        <DataTableShell
          uiId={`${pageId}-result`}
          page={1}
          pageSize={50}
          total={rows.length}
          keyword=""
          loading={loading}
          onPageChange={() => undefined}
          onPageSizeChange={() => undefined}
          onKeywordChange={() => undefined}
          exportEffect={`list:${pageId}#export`}
          onExport={() => {
            void dataSource.list(pageId, { page: 1, pageSize: 50, keyword: '', export: true }).catch(async (cause: unknown) => {
              await dialogs.alert(String(cause));
            });
          }}
        >
          <Table
            uiId={`${pageId}-result-table`}
            columns={columns}
            rows={rows}
            rowKey={rowUiKey}
            emptyText={t('table.empty')}
            renderCell={(row, columnKey) => {
              if (columnKey === 'no') {
                return String(rows.indexOf(row) + 1);
              }
              if (columnKey === 'content') {
                // 題目內容＝fixtures 的 title 語意值（誠實池內容，不虚構業務資料）。
                return String(row['title'] ?? '');
              }
              const raw = row[columnKey];
              return raw === undefined || raw === null ? '' : String(raw);
            }}
          />
        </DataTableShell>
      </ContentPanel>
      {/* 新增題目 modal（:720-866；題型相依區段＝誠實殼＋雛形值標註）。 */}
      <ModalFrame
        uiId={`${pageId}-form-dialog`}
        open={editOpen}
        title="新增題目"
        size="lg"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <Button uiId={`${pageId}-form-dialog-cancel`} variant="default" size="sm" type="button" onClick={() => setEditOpen(false)}>
              關閉
            </Button>
            <Button
              uiId={`${pageId}-form-dialog-save`}
              variant="primary"
              size="sm"
              type="button"
              disabled={loading}
              dataEffect={`update:${pageId}`}
              onClick={() => {
                void (async () => {
                  // saveQuestionAjax（:462）；雛形＝info（誠實標註：欄位完整鏡像＝接真後端批）。
                  try {
                    await dataSource.update(pageId, '0', {});
                    setEditOpen(false);
                    await dialogs.info('存檔成功【雛形值待實機校準】');
                  } catch (cause: unknown) {
                    await dialogs.alert(String(cause));
                  }
                })();
              }}
            >
              存檔
            </Button>
          </>
        }
      >
        <div data-ui-id={`${pageId}-form-dialog-form`} className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="w-36 shrink-0 text-sm">* 題目類型：</label>
            <Select
              uiId={`${pageId}-dialog-question-type`}
              name="questionType"
              value=""
              onChange={() => undefined}
              options={QUESTION_TYPE_OPTIONS}
              placeholder="請選擇"
            />
          </div>
          <div className="flex items-start gap-2">
            <label className="w-36 shrink-0 text-sm">* 題目內容：</label>
            <textarea data-ui-id={`${pageId}-dialog-content`} name="content" className="min-h-24 flex-1 rounded border border-border p-2 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-36 shrink-0 text-sm">* 困難度：</label>
            {(['簡單', '中等', '困難'] as const).map((lv) => (
              <label key={lv} className="inline-flex items-center gap-1 text-sm">
                <input type="radio" name="level" required className="size-4 accent-brand" /> {lv}
              </label>
            ))}
          </div>
          <div className="flex items-start gap-2">
            <label className="w-36 shrink-0 text-sm">選項：</label>
            <Button uiId={`${pageId}-dialog-add-answer`} variant="default" size="sm" type="button">
              新增項目
            </Button>
          </div>
        </div>
      </ModalFrame>
    </div>
  );
}

// ── InitAllQuestionBody ─────────────────────────────────────────────────────
export function InitAllQuestionBody({ pageId }: { pageId: string }): ReactElement {
  const navigate = useNavigate();
  return (
    <div data-ui-id={pageId}>
      <ContentPanel uiId={`${pageId}-panel`} title="題目編輯" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 單題區塊（:455-597；雛形以單題鏡像＝B3-2 誠實標註）。 */}
        <div data-ui-id={`${pageId}-question-block`} className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="mb-[5px] inline-block font-bold text-ink">題目種類：</label>
            <Select uiId={`${pageId}-question-type`} name="questionType" value="select" onChange={() => undefined} options={QUESTION_TYPE_OPTIONS} />
          </div>
          <div className="flex items-start gap-2">
            <label className="w-36 shrink-0 text-sm">* 題目內容：</label>
            <textarea data-ui-id={`${pageId}-content`} name="content" className="min-h-24 flex-1 rounded border border-border p-2 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-36 shrink-0 text-sm">題目圖檔：</label>
            <input type="file" id="file" name="file" data-ui-id={`${pageId}-file`} className="text-xs" />
          </div>
          <div className="flex items-start gap-2">
            <label className="w-36 shrink-0 text-sm">建議答案：</label>
            <textarea data-ui-id={`${pageId}-answer`} name="suggestAnswer" className="min-h-20 flex-1 rounded border border-border p-2 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-36 shrink-0 text-sm">選項：</label>
            <Button uiId={`${pageId}-add-sel-answer`} variant="default" size="sm" type="button">
              新增項目
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-36 shrink-0 text-sm">* 困難度：</label>
            {(['簡單', '中等', '困難'] as const).map((lv) => (
              <label key={lv} className="inline-flex items-center gap-1 text-sm">
                <input type="radio" name="level" required className="size-4 accent-brand" /> {lv}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <label className="w-36 shrink-0 text-sm">* 答案：</label>
            <label className="inline-flex items-center gap-1 text-sm">
              <input type="radio" name="ynAnswer" value="1" required className="size-4 accent-brand" /> 是
            </label>
            <label className="inline-flex items-center gap-1 text-sm">
              <input type="radio" name="ynAnswer" value="0" required className="size-4 accent-brand" /> 否
            </label>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-36 shrink-0 text-sm">是否啟用：</label>
            <input type="checkbox" id="isEnable" name="isEnable" defaultChecked data-ui-id={`${pageId}-is-enable`} className="size-4 accent-brand" /> 啟用
          </div>
        </div>
        <div data-ui-id={`${pageId}-actions`} className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            uiId={`${pageId}-btn-close`}
            variant="default"
            size="sm"
            type="button"
            // :447 window.location.replace('exam/questionBankMain')。
            onClick={() => navigate('/exam/questionBank')}
          >
            關閉
          </Button>
          <Button uiId={`${pageId}-save-btn`} variant="primary" size="sm" type="button" dataEffect={`update:${pageId}`}>
            存檔
          </Button>
        </div>
      </ContentPanel>
    </div>
  );
}

// ── ViewTestingBody ─────────────────────────────────────────────────────────
export function ViewTestingBody({ pageId }: { pageId: string }): ReactElement {
  return (
    <div data-ui-id={pageId}>
      <ContentPanel uiId={`${pageId}-panel`} title="填寫測驗" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 測驗資訊 ibox（:34-46）。 */}
        <div data-ui-id={`${pageId}-info`} className="mb-4">
          <label className="mb-[5px] inline-block font-bold text-ink">
            測驗資訊
          </label>
          <div className="text-sm text-ink">【測驗狀態/參數插值＝接真後端批（#19）】</div>
        </div>
        {/* 作答表單（:101+；hidden id/examInstId/testingMode）＋題目區塊＝誠實殼。 */}
        <form
          data-ui-id={`${pageId}-answer-form`}
          noValidate
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <input type="hidden" id="id" name="id" />
          <input type="hidden" id="testingId" name="examInstId" />
          <input type="hidden" id="testingMode" name="testingMode" />
          <div className="text-sm text-ink">【題目作答區塊（逐題渲染）＝接真後端批（#19）】</div>
        </form>
        {/* 按鈕群（:126-129；mode 相依顯示＝hidden 屬性鏡像）。 */}
        <div data-ui-id={`${pageId}-actions`} className="mt-4 flex flex-wrap items-center gap-2">
                <button type="button" data-ui-id={`${pageId}-btn-do-save`} className="hidden">
        暫存
      </button>
                <button type="button" data-ui-id={`${pageId}-btn-do-answer`} className="hidden">
        交卷
      </button>
          <Button uiId={`${pageId}-btn-close`} variant="default" size="sm" type="button">
            關閉
          </Button>
                <button type="button" data-ui-id={`${pageId}-btn-do-correct`} className="hidden">
        批改
      </button>
        </div>
      </ContentPanel>
    </div>
  );
}

/** 導流連結（questionBank.jsp:73 a.showQuestion 編輯題目 → :119 location.href；站內導航）。 */
export function QuestionBankEditQuestionLink({ row, pageId }: { row: Row; pageId: string }): ReactElement {
  const navigate = useNavigate();
  const id = String(row.id ?? '');
  return (
    <a
      className="text-brand hover:underline"
      href={`/exam/question/${id}`}
      data-ui-id={`${pageId}-row-${toUiKey(id)}-edit-question`}
      onClick={(event) => {
        event.preventDefault();
        navigate(`/exam/question/${id}`);
      }}
    >
      編輯題目
    </a>
  );
}
